import { useMemo, useState } from "react";
import { InputTable, InputColumnTableProps, InputSwitch, Combobox, EInputTypes, InputTextCustom, Modal, ModalWidth } from "medere-ui-framework";
import { Button } from "primereact/button";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import { defaultMedicalScheduleTemplate, defaultQueueNumerator, MedicalScheduleTemplate, QueueNumerator } from "../../../../../interface/interface";
import { getMedicalScheduleTemplateForTotem } from "../../../../../servicesHooks/Location/useLocationConfigService";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";

interface Props {
    queueNumerators: QueueNumerator[];
    onChange: (newQueueNumerators: QueueNumerator[]) => void;
    onDelete: (queueNumerator: QueueNumerator, _idx: number) => void;
    modalWidth?: ModalWidth;
}

interface QueueNumeratorErrors {
    optionDescription?: string;
    queueStringId?: string;
    _order?: string;
    medicalScheduleTemplate?: string;
}

const createDefaultQueueNumerator = (): QueueNumerator => ({ ...defaultQueueNumerator });

export function QueueNumeratorTable({ queueNumerators, onChange, onDelete, modalWidth = "w-medium" }: Props) {
    const isMobile = useResizeListener(768);
    const [queueNumeratorItem, setQueueNumeratorItem] = useState<QueueNumerator>(createDefaultQueueNumerator());
    const [queueNumeratorErrors, setQueueNumeratorErrors] = useState<QueueNumeratorErrors>({});
    const [selectedMedicalScheduleTemplate, setSelectedMedicalScheduleTemplate] = useState<MedicalScheduleTemplate>(defaultMedicalScheduleTemplate);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);

    const normalizedQueueNumerators = useMemo(() => queueNumerators ?? [], [queueNumerators]);

    const isSec = (x: QueueNumerator) => (x.queuestringid || "").toUpperCase() === "S";

    const clearEditor = () => {
        setQueueNumeratorItem(createDefaultQueueNumerator());
        setQueueNumeratorErrors({});
        setSelectedMedicalScheduleTemplate(defaultMedicalScheduleTemplate);
        setEditIndex(null);
    };

    const closeMobileModal = () => {
        setMobileModalVisible(false);
        clearEditor();
    };

    const validateQueueNumerator = (queueNum: QueueNumerator, ignoredIndex: number | null): QueueNumeratorErrors => {
        const errors: QueueNumeratorErrors = {};
        const normalizedOptionDescription = (queueNum.optiondescription ?? "").trim();
        const normalizedQueueStringId = (queueNum.queuestringid ?? "").trim().toUpperCase();
        const normalizedOrder = Number(queueNum._order ?? 0);

        if (!normalizedOptionDescription) errors.optionDescription = "El nombre es requerido";
        if (!normalizedQueueStringId) errors.queueStringId = "Requerido";
        if (normalizedQueueStringId) {
            if (normalizedQueueStringId === "S") {
                errors.queueStringId = "La 'S' está reservada";
            } else {
                const duplicate = normalizedQueueNumerators.some((qn, idx) =>
                    idx !== ignoredIndex && (qn.queuestringid ?? "").trim().toUpperCase() === normalizedQueueStringId
                );
                if (duplicate) errors.queueStringId = "Letra repetida";
            }
        }
        if (!normalizedOrder || normalizedOrder <= 0) errors._order = "Requerido";
        if (Number(queueNum.medicalscheduletemplate ?? 0) <= 0) errors.medicalScheduleTemplate = "Este campo es requerido";
        return errors;
    };

    const addOrUpdateWaitingQueue = async () => {
        const errors = validateQueueNumerator(queueNumeratorItem, editIndex);
        setQueueNumeratorErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) return false;

        const normalizedItem: QueueNumerator = {
            ...queueNumeratorItem,
            optiondescription: (queueNumeratorItem.optiondescription ?? "").trim(),
            queuestringid: (queueNumeratorItem.queuestringid ?? "").trim().toUpperCase(),
            _order: Number(queueNumeratorItem._order),
        };

        if (editIndex === null) {
            onChange([...normalizedQueueNumerators, normalizedItem]);
        } else {
            onChange(normalizedQueueNumerators.map((item, index) => index === editIndex ? normalizedItem : item));
        }

        if (isMobile) {
            closeMobileModal();
        } else {
            clearEditor();
        }
        return true;
    };

    const handleEditWaitingQueue = (item: QueueNumerator, index: number, openMobileModal = false) => {
        if (isSec(item)) return;
        setQueueNumeratorItem({
            ...item,
            optiondescription: item.optiondescription ?? "",
            queuestringid: item.queuestringid ?? "",
            _order: Number(item._order ?? 0),
        });
        setSelectedMedicalScheduleTemplate({
            medicalscheduletemplate: item.medicalscheduletemplate,
            description: item.medicalscheduletemplatedesc
        });
        setQueueNumeratorErrors({});
        setEditIndex(index);
        if (openMobileModal) setMobileModalVisible(true);
    };

    const handleDeleteWaitingQueue = (item: QueueNumerator, index: number) => {
        onDelete(item, index);
        if (editIndex === null) return;
        if (editIndex === index) {
            clearEditor();
            return;
        }
        if (index < editIndex) setEditIndex(editIndex - 1);
    };

    const handleToggleActiveWaitingQueue = (item: QueueNumerator, index: number) => {
        if (isSec(item)) return;
        const updated = [...normalizedQueueNumerators];
        updated[index] = { ...item, active: !item.active };
        onChange(updated);
    };

    const queueNumeratorCols: InputColumnTableProps<QueueNumerator>[] = [
        {
            header: "Descripción de la opción",
            className: "w-1/3",
            body: (qn) => qn.optiondescription,
            input: (
                <InputTextCustom
                    key={"inputQNnameid"}
                    className={(queueNumeratorErrors.optionDescription ? "" : "border2") + " w-full"}
                    value={queueNumeratorItem.optiondescription}
                    onChange={(e) => {
                        setQueueNumeratorItem({ ...queueNumeratorItem, optiondescription: e.target.value });
                        setQueueNumeratorErrors({ ...queueNumeratorErrors, optionDescription: "" });
                    }}
                    error={!!queueNumeratorErrors.optionDescription}
                    caption={queueNumeratorErrors.optionDescription}
                />
            )
        }, {
            header: "Letra",
            className: "w-1/12",
            body: (qn) => qn.queuestringid,
            input: (
                <InputTextCustom
                    key={"inputQNletterid"}
                    className={(queueNumeratorErrors.queueStringId ? "" : "border2") + " w-full"}
                    value={queueNumeratorItem.queuestringid}
                    onChange={(e) => {
                        setQueueNumeratorItem({ ...queueNumeratorItem, queuestringid: e.target.value });
                        setQueueNumeratorErrors({ ...queueNumeratorErrors, queueStringId: "" });
                    }}
                    error={!!queueNumeratorErrors.queueStringId}
                    caption={queueNumeratorErrors.queueStringId}
                />
            )
        }, {
            header: "N° orden",
            className: "w-1/12",
            body: (qn) => qn._order,
            input: (
                <InputTextCustom
                    key={"inputQNorderid"}
                    className={(queueNumeratorErrors._order ? "" : "border2") + " w-full"}
                    value={queueNumeratorItem._order}
                    onChange={(e) => {
                        setQueueNumeratorItem({ ...queueNumeratorItem, _order: e.target.value as unknown as number });
                        setQueueNumeratorErrors({ ...queueNumeratorErrors, _order: "" });
                    }}
                    inputType={EInputTypes.NUMBER}
                    error={!!queueNumeratorErrors._order}
                    caption={queueNumeratorErrors._order}
                />
            )
        }, {
            header: "Asociar con:",
            className: "w-1/4",
            body: (qn) => qn.medicalscheduletemplatedesc,
            input: (
                <Combobox
                    key={"combomedicalschedulesetudid"}
                    className={(queueNumeratorErrors.medicalScheduleTemplate ? "" : "border2") + " w-full"}
                    getItems={getMedicalScheduleTemplateForTotem}
                    value={selectedMedicalScheduleTemplate}
                    setValue={(e: MedicalScheduleTemplate) => {
                        setSelectedMedicalScheduleTemplate(e);
                        setQueueNumeratorItem({
                            ...queueNumeratorItem,
                            medicalscheduletemplate: e.medicalscheduletemplate,
                            medicalscheduletemplatedesc: e.description
                        });
                        setQueueNumeratorErrors({ ...queueNumeratorErrors, medicalScheduleTemplate: "" });
                    }}
                    optionLabel="description"
                    error={!!queueNumeratorErrors.medicalScheduleTemplate}
                    caption={queueNumeratorErrors.medicalScheduleTemplate}
                />
            )
        }, {
            header: "Estado",
            className: "w-1/12",
            body: (qn) => (<span>{qn.active ? "Activo" : "Inactivo"}</span>),
            input: (
                <div className="flex align-items-center gap-2 mt-2 location-switch-inline-row">
                    <InputSwitch
                        id="inputswitchactiveid"
                        className="w-full"
                        checked={queueNumeratorItem.active}
                        onChange={(e) => setQueueNumeratorItem({ ...queueNumeratorItem, active: e.value })}
                    />
                    <span className="text-sm location-switch-state-label">{queueNumeratorItem.active ? "Activo" : "Inactivo"}</span>
                </div>
            ),
        }, {
            header: "",
            className: "mleft",
            body: (qn, index) => {
                const sec = isSec(qn);
                const menuItems: LocationActionItem[] = [
                    { label: "Editar", icon: "pi pi-file-edit", action: () => handleEditWaitingQueue(qn, index) },
                    {
                        label: qn.active ? "Desactivar" : "Activar",
                        icon: qn.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        action: () => handleToggleActiveWaitingQueue(qn, index),
                    },
                    { label: "Eliminar", icon: "pi pi-trash", action: () => handleDeleteWaitingQueue(qn, index) },
                ];

                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu
                            items={menuItems}
                            ariaLabel="Opciones del contenido"
                            buttonId="buttonmenuitemid"
                            buttonDisabled={sec}
                        />
                    </div>
                );
            },
        },
    ];

    if (!isMobile) {
        return (
            <div>
                <InputTable
                    columnGap={4}
                    iconAddBtn="pi pi-plus-circle text-2xl"
                    headerDivClassName="font-semibold bg-bluegray-50"
                    dividerClassName="my-1 border-divider"
                    buttonColumnWidth={40}
                    items={normalizedQueueNumerators}
                    columns={queueNumeratorCols}
                    onAdd={addOrUpdateWaitingQueue}
                    divider
                    dividerInFirstItem
                    dividerInLastItem
                />
            </div>
        );
    }

    function formatCardValue(value: string | number | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    return (
        <div>
            <div className="location-mobile-inline-actions">
                <Button
                    className="location-mobile-add-btn"
                    icon="pi pi-plus-circle"
                    label="Agregar"
                    onClick={() => {
                        clearEditor();
                        setMobileModalVisible(true);
                    }}
                />
            </div>

            <div className="location-mobile-card-list">
                {normalizedQueueNumerators.map((item, index) => {
                    const sec = isSec(item);
                    const menuItems: LocationMobileActionItem[] = [
                        { label: "Editar", icon: "pi pi-file-edit", action: () => handleEditWaitingQueue(item, index, true), disabled: sec },
                        {
                            label: item.active ? "Desactivar" : "Activar",
                            icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                            action: () => handleToggleActiveWaitingQueue(item, index),
                            disabled: sec
                        },
                        { label: "Eliminar", icon: "pi pi-trash", action: () => handleDeleteWaitingQueue(item, index), disabled: sec },
                    ];

                    return (
                        <article key={`queue-number-${item.queuenumerator}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{formatCardValue(item.optiondescription, "Sin descripción")}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del contenido" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Letra</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.queuestringid)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">N° orden</span>
                                <span className="location-mobile-card-value">{formatCardValue(item._order)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Asociar con</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.medicalscheduletemplatedesc)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Estado</span>
                                <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                            </div>
                        </article>
                    );
                })}
                {normalizedQueueNumerators.length === 0 && (
                    <p className="location-mobile-empty">No hay contenido de pantalla cargado.</p>
                )}
            </div>

            <Modal
                className="location-mobile-editor-modal location-mobile-nested-editor-modal"
                visible={mobileModalVisible}
                setVisible={setMobileModalVisible}
                onHideCustom={closeMobileModal}
                width={modalWidth}
                header={<h1 className="info-dark text-2xl">{editIndex === null ? "Agregar contenido" : "Editar contenido"}</h1>}
            >
                <div className="location-config-view location-mobile-modal-content">
                    <div className="location-mobile-modal-fields">
                        <div>
                            <p>Descripción de la opción</p>
                            <InputTextCustom
                                className={(queueNumeratorErrors.optionDescription ? "" : "border2") + " w-full"}
                                value={queueNumeratorItem.optiondescription}
                                onChange={(e) => {
                                    setQueueNumeratorItem({ ...queueNumeratorItem, optiondescription: e.target.value });
                                    setQueueNumeratorErrors({ ...queueNumeratorErrors, optionDescription: "" });
                                }}
                                error={!!queueNumeratorErrors.optionDescription}
                                caption={queueNumeratorErrors.optionDescription}
                            />
                        </div>
                        <div>
                            <p>Letra</p>
                            <InputTextCustom
                                className={(queueNumeratorErrors.queueStringId ? "" : "border2") + " w-full"}
                                value={queueNumeratorItem.queuestringid}
                                onChange={(e) => {
                                    setQueueNumeratorItem({ ...queueNumeratorItem, queuestringid: e.target.value });
                                    setQueueNumeratorErrors({ ...queueNumeratorErrors, queueStringId: "" });
                                }}
                                error={!!queueNumeratorErrors.queueStringId}
                                caption={queueNumeratorErrors.queueStringId}
                            />
                        </div>
                        <div>
                            <p>N° orden</p>
                            <InputTextCustom
                                className={(queueNumeratorErrors._order ? "" : "border2") + " w-full"}
                                value={queueNumeratorItem._order}
                                onChange={(e) => {
                                    setQueueNumeratorItem({ ...queueNumeratorItem, _order: e.target.value as unknown as number });
                                    setQueueNumeratorErrors({ ...queueNumeratorErrors, _order: "" });
                                }}
                                inputType={EInputTypes.NUMBER}
                                error={!!queueNumeratorErrors._order}
                                caption={queueNumeratorErrors._order}
                            />
                        </div>
                        <div>
                            <p>Asociar con</p>
                            <Combobox
                                className={(queueNumeratorErrors.medicalScheduleTemplate ? "" : "border2") + " w-full"}
                                getItems={getMedicalScheduleTemplateForTotem}
                                value={selectedMedicalScheduleTemplate}
                                setValue={(e: MedicalScheduleTemplate) => {
                                    setSelectedMedicalScheduleTemplate(e);
                                    setQueueNumeratorItem({
                                        ...queueNumeratorItem,
                                        medicalscheduletemplate: e.medicalscheduletemplate,
                                        medicalscheduletemplatedesc: e.description
                                    });
                                    setQueueNumeratorErrors({ ...queueNumeratorErrors, medicalScheduleTemplate: "" });
                                }}
                                optionLabel="description"
                                error={!!queueNumeratorErrors.medicalScheduleTemplate}
                                caption={queueNumeratorErrors.medicalScheduleTemplate}
                            />
                        </div>
                        <div className="flex align-items-center gap-2 location-switch-inline-row">
                            <InputSwitch
                                id="inputswitchactivemobileid"
                                checked={queueNumeratorItem.active}
                                onChange={(e) => setQueueNumeratorItem({ ...queueNumeratorItem, active: e.value })}
                            />
                            <span className="text-sm location-switch-state-label">{queueNumeratorItem.active ? "Activo" : "Inactivo"}</span>
                        </div>
                    </div>
                    <div className="flex justify-content-end align-items-center gap-2 mt-2 location-mobile-modal-actions">
                        <Button label="Cancelar" onClick={closeMobileModal} className="btnCancel2 btnWidth" />
                        <Button label={editIndex === null ? "Agregar" : "Guardar"} onClick={() => { void addOrUpdateWaitingQueue(); }} className="btnContinue btnWidth" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}


